import { useCallback, useRef, useState } from "react";
import { logError } from "./log";
import { haptic } from "./native";

// Rear camera stream tuned for paper: high resolution, continuous focus, optional torch.
export const useCamera = (videoRef) => {
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [torch, setTorch] = useState({ supported: false, on: false });
  const [size, setSize] = useState({ w: 4, h: 3 });

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setReady(false);
    setTorch({ supported: false, on: false });
  }, []);

  const start = useCallback(async () => {
    setError("");
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setError("Live camera isn't available here — use “Take photo” below to open your phone's camera app.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 2560 }, height: { ideal: 1920 } },
        audio: false,
      });
      streamRef.current = stream;
      const track = stream.getVideoTracks()[0];
      const caps = track.getCapabilities?.() || {};
      if (Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
        track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
      }
      setTorch({ supported: Boolean(caps.torch), on: false });
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        await new Promise((res) => {
          if (v.readyState >= 1 && v.videoWidth) return res();
          v.onloadedmetadata = () => res();
          setTimeout(res, 2500);
        });
        await v.play().catch((e) => logError("video autoplay blocked", e));
        if (v.videoWidth && v.videoHeight) setSize({ w: v.videoWidth, h: v.videoHeight });
      }
      setReady(true);
    } catch (e) {
      setError(e?.name === "NotAllowedError" ? "Camera permission was denied — allow it in your browser settings, or use “Take photo” below." : "Couldn't open the live camera — use “Take photo” below instead.");
    }
  }, [videoRef]);

  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const on = !torch.on;
    try {
      await track.applyConstraints({ advanced: [{ torch: on }] });
      setTorch((t) => ({ ...t, on }));
    } catch (e) {
      logError("torch toggle failed", e);
    }
  }, [torch.on]);

  const snap = useCallback(async () => {
    const v = videoRef.current;
    if (!v || !ready || !v.videoWidth) return null;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    navigator.vibrate?.(20);
    haptic("Medium");
    return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
  }, [videoRef, ready]);

  return { ready, error, torch, size, start, stop, snap, toggleTorch };
};
